import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Building2, Mail, RefreshCw, Search, UserRound } from 'lucide-react';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';
import { memberService, type AdminMember } from '../../services/memberService';
import { getErrorMessage } from '../../utils/error';
import { useLanguage } from '../../features/language/useLanguage';

export const AdminMemberSettings: React.FC = () => {
  const { t } = useLanguage();
  const [adminMembers, setAdminMembers] = useState<AdminMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { page, size, offset, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([search]);

  const loadAdminMembers = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const data = await memberService.getAdminMembers();
      setAdminMembers(Array.isArray(data) ? data : []);
    } catch (error) {
      setErrorMessage(getErrorMessage(error, t('settingsAdminMembers.errors.loadFailed')));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadAdminMembers();
  }, [loadAdminMembers]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return adminMembers;

    return adminMembers.filter((m) => {
      const email = m.email?.toLowerCase() ?? '';
      const fullName = m.full_name?.toLowerCase() ?? '';
      const company = m.company_name?.toLowerCase() ?? '';
      return email.includes(q) || fullName.includes(q) || company.includes(q);
    });
  }, [adminMembers, search]);

  const total = filteredMembers.length;
  const paginatedMembers = useMemo(
    () => filteredMembers.slice(offset, offset + size),
    [filteredMembers, offset, size]
  );

  return (
    <div className="space-y-6" data-test-id="admin-member-settings-page">
      <div className="flex flex-col gap-1" data-test-id="admin-member-page-header">
        <h1 className="text-2xl font-semibold text-gray-900">{t('settingsAdminMembers.title')}</h1>
        <p className="text-sm text-gray-500">{t('settingsAdminMembers.subtitle')}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between" data-test-id="admin-member-filters">
        <div className="relative w-full sm:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('settingsAdminMembers.searchPlaceholder')}
            disabled={isLoading || Boolean(errorMessage)}
            data-test-id="admin-member-search-input"
            className="w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-60"
          />
        </div>
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200" data-test-id="admin-member-table-card">
        {isLoading ? (
          <LoadingSpinner text={t('settingsAdminMembers.loading')} />
        ) : errorMessage ? (
          <div className="p-8 text-center" data-test-id="admin-member-error-state">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-5 w-5 text-red-600" />
            </div>
            <p className="text-sm font-medium text-gray-900">{t('settingsAdminMembers.errors.unableLoad')}</p>
            <p className="mt-1 text-sm text-gray-500">{errorMessage}</p>
            <button
              type="button"
              onClick={() => void loadAdminMembers()}
              data-test-id="admin-member-retry-button"
              className="mx-auto mt-4 inline-flex items-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('settingsAdminMembers.retry')}
            </button>
          </div>
        ) : (
          <>
            {total === 0 ? (
              <div className="p-10 text-center text-gray-500" data-test-id="admin-member-empty-state">
                {search.trim() ? t('settingsAdminMembers.noResults') : t('settingsAdminMembers.empty')}
              </div>
            ) : (
              <div className="overflow-x-auto" data-test-id="admin-member-table-wrapper">
                <table className="min-w-full divide-y divide-gray-200" data-test-id="admin-member-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{t('forms.common.email')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{t('settingsMember.fields.fullName')}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">{t('settingsAdminMembers.companyName')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedMembers.map((member) => (
                      <tr key={member.id} className="hover:bg-gray-50" data-test-id={`admin-member-row-${member.id}`}>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-900">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-400" />
                            <span data-test-id={`admin-member-email-${member.id}`}>{member.email}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            <UserRound className="h-4 w-4 text-gray-400" />
                            <span data-test-id={`admin-member-full-name-${member.id}`}>{member.full_name || t('common.table.na')}</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" />
                            <span data-test-id={`admin-member-company-${member.id}`}>{member.company_name || t('settingsAdminMembers.unknownCompany')}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={page}
              pageSize={size}
              total={total}
              onPageChange={setPage}
              onPageSizeChange={setSize}
              isLoading={isLoading}
              className="border-t"
            />
          </>
        )}
      </div>
    </div>
  );
};
